import {
  Award,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  Edit as EditIcon,
  Eye,
  FileText,
  GripVertical,
  Headphones,
  Layers,
  Loader2,
  MessageSquare,
  MonitorPlay,
  Plus,
  RefreshCw,
  Repeat,
  Save,
  Shuffle,
  Trash2,
  Video,
  X,
  AlertTriangle,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Services
import { courseService } from '@/services/course.service';
import { getLanguageCode } from '@/utils/languageMapper';

// Course Stage type
type CourseStage = 'generating' | 'draft' | 'published' | 'unpublished';

// Types
import {
  ContentFormatEnum,
  CourseDetails,
  CourseGenerationSettings,
  CourseMetadata,
  Duration,
  Module,
  ModuleDetails,
  SlideThemeEnum
} from '@/types/course.types';

// Components
import ContentItems from '@/components/courses/EditCourse/ContentItems';
import CourseHeader from '@/components/courses/EditCourse/CourseHeader';
import MetadataModal from '@/components/courses/EditCourse/MetadataModal';
import ModuleLearningObjectives from '@/components/courses/EditCourse/ModuleLearningObjectives';
import EditAssessmentQuestionModal from '@/components/courses/EditCourse/EditAssessmentQuestionModal';
import AddAssessmentQuestionModal from '@/components/courses/EditCourse/AddAssessmentQuestionModal';
import SortableModule from '@/components/courses/EditCourse/SortableModule';
import ModuleFormModal, { type ModuleFormValues } from '@/components/courses/EditCourse/ModuleFormModal';
import AddAssessmentModal, { type AssessmentLevel, type AssessmentType } from '@/components/courses/EditCourse/AddAssessmentModal';
import { useCourseTranslations } from '@/hooks/useCourseTranslations';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import { useQueryClient } from '@tanstack/react-query';
import useHasPermission from '@/hooks/useHasPermission';
import { COURSE_DELETE_PERMISSION } from '@/constants/constant-permissions';

export default function EditCourse() {
  const { id } = useParams<{ id: string }>();

  // Which language version is on screen. Kept in the URL rather than in
  // component state so the view survives a refresh and can be shared as a
  // link -- and so `id` stays the MASTER course in every language.
  const [searchParams] = useSearchParams();
  const activeLanguageCode = searchParams.get('language') || undefined;

  // ?panel=translations opens the Translations tab straight away. The
  // partial-translation notice links here so "Review & retry" lands on the
  // retry controls instead of on the page that merely contains them.
  const requestedPanel = searchParams.get('panel') || undefined;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Deleting a course is a separate grant from editing it.
  const canDeleteCourse = useHasPermission([COURSE_DELETE_PERMISSION.COURSE_DELETE]);

  // State
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [courseMetadata, setCourseMetadata] = useState<CourseMetadata>({});
  const [isLoading, setIsLoading] = useState(true);
  // SCORM courses are imported and not editable — block the editor with a notice.
  const [isScormCourse, setIsScormCourse] = useState(false);
  const [expandedModules, setExpandedModules] = useState<string[]>([]);
  const [moduleContents, setModuleContents] = useState<{ [moduleId: string]: ModuleDetails }>({});
  const [loadingModules, setLoadingModules] = useState<string[]>([]);
  const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);
  // Which tab the metadata modal opens on — "Manage translations" deep-links
  // to the Translations tab, everything else opens on Basic Info.
  const [metadataInitialTab, setMetadataInitialTab] = useState('basic');

  // Language versions of this course family. Queried against the MASTER id so
  // the header switcher works from any language — on a translation, the master
  // is source_course_id; on the original, it is the course's own id.
  const masterCourseId = courseDetails?.source_course_id || id;
  const isMasterCourse = !!courseDetails && !courseDetails.source_course_id && !isScormCourse;
  const { data: courseTranslations } = useCourseTranslations(
    masterCourseId,
    !!courseDetails && !isScormCourse
  );

  /** Lessons that failed to translate into the language currently on screen. */
  const failedTranslation = useMemo(() => {
    if (!activeLanguageCode) return { count: 0, items: [] as Array<{ module: string; lesson: string; reason?: string }> };
    const row = (courseTranslations || []).find(
      (t: any) => t.language_code?.toLowerCase() === activeLanguageCode.toLowerCase()
    );
    return {
      // The COUNT drives the notice. It comes from the list endpoint, which is
      // what this page already loads.
      count: row?.progress?.failed_pieces || 0,
      // The named lessons are a bonus: only the DETAIL endpoint carries them, so
      // they may be absent here. The notice must still appear without them —
      // "1 lesson failed" is far more useful than silence.
      items: (row?.failed_items || []) as Array<{ module: string; lesson: string; reason?: string }>,
    };
  }, [courseTranslations, activeLanguageCode]);

  /** True when the original changed after this language was produced. The
   *  Translations tab lives only on the master, so without this the translated
   *  view gives no hint that it is behind and no route to fix it. */
  const isTranslationStale = useMemo(() => {
    if (!activeLanguageCode) return false;
    const row = (courseTranslations || []).find(
      (t: any) => t.language_code?.toLowerCase() === activeLanguageCode.toLowerCase()
    );
    return Boolean(row?.is_stale);
  }, [courseTranslations, activeLanguageCode]);

  /** This language is being re-translated right now. The page still serves the
   *  PREVIOUS version, which is complete — say so rather than look current. */
  const isTranslationUpdating = useMemo(() => {
    if (!activeLanguageCode) return false;
    const row = (courseTranslations || []).find(
      (t: any) => t.language_code?.toLowerCase() === activeLanguageCode.toLowerCase()
    );
    const status = String((row as any)?.status ?? '').toUpperCase();
    return status === 'IN_PROGRESS' || status === 'PENDING';
  }, [courseTranslations, activeLanguageCode]);

  const openMetadata = (tab: string = 'basic') => {
    setMetadataInitialTab(tab);
    setIsMetadataModalOpen(true);
  };

  const [expandedSections, setExpandedSections] = useState({
    objectives: true,
    content: true
  });

  // Assessment modal state
  const [assessmentModalState, setAssessmentModalState] = useState<{
    isOpen: boolean;
    assessmentId: string | null;
    assessmentData: any | null;
    loading: boolean;
  }>({
    isOpen: false,
    assessmentId: null,
    assessmentData: null,
    loading: false,
  });

  // Editing question state for assessment modal
  const [editingAssessmentQuestion, setEditingAssessmentQuestion] = useState<{
    question: any;
    allQuestions: any[];
    assessmentId: string;
  } | null>(null);

  // Add question modal state
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);

  // Delete question confirmation state
  const [deletingQuestion, setDeletingQuestion] = useState<{
    questionId: string;
    questionText: string;
  } | null>(null);
  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);

  // Course-level assessment state
  const [coursePreAssessmentDetails, setCoursePreAssessmentDetails] = useState<any | null>(null);
  const [loadingCoursePreAssessment, setLoadingCoursePreAssessment] = useState(false);
  const [courseAssessmentDetails, setCourseAssessmentDetails] = useState<any | null>(null);
  const [loadingCourseAssessment, setLoadingCourseAssessment] = useState(false);

  // ── Manual module editing (add / edit / delete / reorder) ──────────────────
  const [moduleFormState, setModuleFormState] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit';
    module: Module | null;
    insertIndex: number;
  }>({ isOpen: false, mode: 'add', module: null, insertIndex: 0 });
  const [deletingModule, setDeletingModule] = useState<Module | null>(null);
  const [isDeletingModule, setIsDeletingModule] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  // Modules can only be edited on a finished, non-SCORM course.
  // Avatar-video courses are treated as constant structure like SCORM (their
  // content is expensive avatar renders): no add/edit/delete of modules or
  // content items — only reorder + publish + metadata.
  const _deliveryType = courseDetails?.generation_settings?.content_delivery_type as string | undefined;
  const isAvatarVideoCourse = _deliveryType === 'avatar_video' || _deliveryType === 'avatar_presentation';
  // Structure is locked (no add/edit/delete) for SCORM and avatar-video courses.
  const isStructureLocked = isScormCourse || isAvatarVideoCourse;

  /**
   * A translation is READ ONLY. This is a data-safety guard, not a preference.
   * Every id on this page is the MASTER's — that is deliberate, so progress,
   * enrolment and deep links survive a language switch.
   */
  const isViewingTranslation = !!courseDetails?.source_course_id;

  // Publishing is one-way and final: a published course is fully read-only, so it
  // blocks every edit path below (modules, content, scenes, assessments, metadata).
  const isPublished = courseDetails?.stage === 'published';

  const isReadOnly = isPublished || isViewingTranslation;

  const canEditModules = !isStructureLocked && courseDetails?.stage !== 'generating' && !isReadOnly;
  // Reorder is allowed even on locked (SCORM / avatar) courses (rearranging only),
  // but not once published.
  const canReorderModules = courseDetails?.stage !== 'generating' && !isReadOnly;

  // Manual assessment creation is allowed on all AI-generated courses (incl.
  // avatar/heygen) — only SCORM (imported, constant) is excluded, and never
  // while the course is still generating or after it is published.
  const canManageAssessments = !isScormCourse && courseDetails?.stage !== 'generating' && !isReadOnly;

  // Add-assessment modal state (course / module / content-item level).
  const [addAssessmentState, setAddAssessmentState] = useState<{
    isOpen: boolean;
    level: AssessmentLevel;
    type: AssessmentType;
    entityId: string;
    moduleId?: string;
  }>({ isOpen: false, level: 'COURSE', type: 'PRE', entityId: '' });

  const openAddAssessment = (
    level: AssessmentLevel,
    type: AssessmentType,
    entityId: string,
    moduleId?: string
  ) => {
    setAddAssessmentState({ isOpen: true, level, type, entityId, moduleId });
  };

  const closeAddAssessment = () => setAddAssessmentState(prev => ({ ...prev, isOpen: false }));

  const handleCreateAssessment = async (data: { title: string; description?: string }) => {
    const { level, type, entityId, moduleId } = addAssessmentState;
    try {
      await courseService.createAssessment({
        assessment_level: level,
        assessment_type: type,
        entity_id: entityId,
        title: data.title,
        description: data.description,
      });
      toast.success('Assessment created');
      closeAddAssessment();
      // Refresh the affected level so the new assessment appears.
      if (level === 'COURSE') {
        if (id) await fetchCourseDetails(id, true);
      } else if (level === 'MODULE') {
        await refetchModuleContent(entityId);
      } else if (level === 'CONTENT_ITEM' && moduleId) {
        await refetchModuleContent(moduleId);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.message ||
          'Failed to create assessment'
      );
      // Rethrow so the modal stays open.
      throw error;
    }
  };

  // Soft-delete a whole assessment (pre/post) at any level.
  const [deletingAssessment, setDeletingAssessment] = useState<{
    assessmentId: string;
    level: AssessmentLevel;
    moduleId?: string;
    label: string;
  } | null>(null);
  const [isDeletingAssessment, setIsDeletingAssessment] = useState(false);

  const requestDeleteAssessment = (
    assessmentId: string,
    level: AssessmentLevel,
    label: string,
    moduleId?: string
  ) => {
    setDeletingAssessment({ assessmentId, level, label, moduleId });
  };

  const handleDeleteAssessment = async () => {
    if (!deletingAssessment) return;
    const { assessmentId, level, moduleId } = deletingAssessment;
    setIsDeletingAssessment(true);
    try {
      await courseService.deleteAssessment(assessmentId);
      toast.success('Assessment deleted');
      setDeletingAssessment(null);
      if (level === 'COURSE') {
        if (id) await fetchCourseDetails(id, true);
      } else if (moduleId) {
        await refetchModuleContent(moduleId);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.message ||
          'Failed to delete assessment'
      );
    } finally {
      setIsDeletingAssessment(false);
    }
  };

  // Drag sensors: a small distance threshold keeps clicks on the header/handle
  // from being interpreted as drags. Keyboard sensor for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Stable sortable id per module (falls back to index for unsaved modules).
  // Must match `moduleKey` used for the SortableModule id in render.
  const getModuleSortableId = (module: Module, index: number): string =>
    module._id || index.toString();

  const openAddModule = (insertIndex: number) => {
    setModuleFormState({ isOpen: true, mode: 'add', module: null, insertIndex });
  };

  const openEditModule = (module: Module) => {
    setModuleFormState({ isOpen: true, mode: 'edit', module, insertIndex: 0 });
  };

  const closeModuleForm = () => {
    setModuleFormState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleSaveModule = async (data: ModuleFormValues) => {
    if (!id) return;
    const editingModuleId = moduleFormState.mode === 'edit' ? moduleFormState.module?._id : null;
    try {
      if (moduleFormState.mode === 'add') {
        await courseService.createModule(id, { ...data, order: moduleFormState.insertIndex });
        toast.success('Module added');
      } else if (editingModuleId) {
        await courseService.updateModule(editingModuleId, data);
        toast.success('Module updated');
      }
      closeModuleForm();
      // Preserve which modules are open — don't jump back to module 1.
      await fetchCourseDetails(id, true);
      // If the edited module is open, refresh its cached content tree so the
      // updated title / objectives don't show stale.
      if (editingModuleId && expandedModules.includes(editingModuleId)) {
        await fetchModuleContent(editingModuleId, true);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.message ||
          'Failed to save module'
      );
      // Rethrow so the modal stays open and preserves the user's input.
      throw error;
    }
  };

  const handleDeleteModuleConfirm = async () => {
    if (!deletingModule?._id || !id) return;
    const deletedId = deletingModule._id;
    setIsDeletingModule(true);
    try {
      await courseService.deleteModule(deletedId);
      toast.success('Module deleted');
      setDeletingModule(null);
      // Collapse + drop the removed module, and keep the rest of the expansion
      // state intact instead of snapping back to module 1.
      setExpandedModules(prev => prev.filter(mid => mid !== deletedId));
      setModuleContents(prev => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });
      await fetchCourseDetails(id, true);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to delete module'
      );
    } finally {
      setIsDeletingModule(false);
    }
  };

  const handleModuleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !id) return;

    const modules = courseDetails?.modules || [];
    const ids = modules.map((m, i) => getModuleSortableId(m, i));
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = modules;
    const reordered = arrayMove(modules, oldIndex, newIndex);

    // Optimistic update, then persist. Revert on failure.
    setCourseDetails((cd) => (cd ? { ...cd, modules: reordered } : cd));
    setIsReordering(true);
    try {
      const orderedIds = reordered.map((m) => m._id).filter(Boolean) as string[];
      await courseService.reorderModules(id, orderedIds);
      toast.success('Module order updated');
    } catch (error: any) {
      setCourseDetails((cd) => (cd ? { ...cd, modules: previous } : cd));
      toast.error(
        error?.response?.data?.error?.error_description || 'Failed to reorder modules'
      );
    } finally {
      setIsReordering(false);
    }
  };

  // Fetch course details.
  useEffect(() => {
    if (id) {
      fetchCourseDetails(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeLanguageCode]);

  // Re-translation just finished: pull the new content in. The translations
  // list already polls while one is running, so this needs no timer of its own.
  const wasUpdatingRef = useRef(false);
  useEffect(() => {
    if (wasUpdatingRef.current && !isTranslationUpdating && id) {
      fetchCourseDetails(id, true);
    }
    wasUpdatingRef.current = isTranslationUpdating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTranslationUpdating, id]);

  // Open the requested panel once the course is loaded. Waits for
  // isMasterCourse because the Translations tab only renders on the master —
  // firing earlier would open the modal on an empty tab.
  useEffect(() => {
    if (requestedPanel === 'translations' && isMasterCourse) {
      openMetadata('translations');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedPanel, isMasterCourse]);

  // Reset scroll to top once loading is complete and content is rendered
  useEffect(() => {
    if (!isLoading) {
      window.scrollTo(0, 0);
    }
  }, [isLoading]);

  // Fetch course-level pre-assessment details when course is loaded
  useEffect(() => {
    const fetchCoursePreAssessment = async () => {
      if (courseDetails?.pre_assessment?.id) {
        setLoadingCoursePreAssessment(true);
        try {
          const response = await courseService.getAssessmentAdmin(courseDetails.pre_assessment.id, activeLanguageCode);
          const assessmentData = response.data || response;
          setCoursePreAssessmentDetails(assessmentData);
        } catch (error) {
          console.error('Error fetching course pre-assessment:', error);
          toast.error('Failed to load course pre-assessment details');
        } finally {
          setLoadingCoursePreAssessment(false);
        }
      }
    };

    fetchCoursePreAssessment();
    // Language belongs here: the id is the MASTER's and does not change when
    // the language does, so without it the old language stays on screen.
  }, [courseDetails?.pre_assessment?.id, activeLanguageCode]);

  // Fetch course-level post-assessment details when course is loaded
  useEffect(() => {
    const fetchCourseAssessment = async () => {
      if (courseDetails?.post_assessment?.id) {
        setLoadingCourseAssessment(true);
        try {
          const response = await courseService.getAssessmentAdmin(courseDetails.post_assessment.id, activeLanguageCode);
          const assessmentData = response.data || response;
          setCourseAssessmentDetails(assessmentData);
        } catch (error) {
          console.error('Error fetching course assessment:', error);
          toast.error('Failed to load course assessment details');
        } finally {
          setLoadingCourseAssessment(false);
        }
      }
    };

    fetchCourseAssessment();
  }, [courseDetails?.post_assessment?.id, activeLanguageCode]);


  const fetchCourseDetails = async (courseId: string, preserveExpansion = false) => {
    try {
      setIsLoading(true);
      const response = await courseService.getCourseById(courseId, activeLanguageCode);

      if (response?.data?.data) {
        const data = response.data.data;
        setCourseDetails(data);

        // SCORM courses cannot be edited — flag it so the editor is replaced with a notice.
        if ((data as any)?.source_type === 'scorm_imported') {
          setIsScormCourse(true);
        }

        // On the initial load, expand the first module by default. On refetches
        // triggered by module edit/delete/reorder, keep whatever is currently
        // open (preserveExpansion) so an operation doesn't jump back to module 1.
        if (!preserveExpansion && data.modules && data.modules.length > 0) {
          const firstModuleId = data.modules[0]._id || '0';
          setExpandedModules([firstModuleId]);
          fetchModuleContent(firstModuleId);
        }

        // Extract metadata
        setCourseMetadata({
          category: data.category,
          level: data.difficulty,
          language: data.language,
          stage: data.stage,
          tags: data.tags,
          prerequisites: data.prerequisites,
          skills: data.targetAudience || data.target_audience,
          certificate_available: false,
          rating: 0,
          total_ratings: 0,
          total_enrollments: 0,
          completion_rate: 0,
          version: '1.0.0',
          last_updated: new Date().toISOString().split('T')[0]
        });

      } else {
        console.error('No course data in response:', response);
        throw new Error('No course data received from API');
      }
    } catch (error: unknown) {
      console.error('Error fetching course details:', error);
      const err = error as Error & { response?: { data?: unknown; status?: number } };
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      toast.error(`Failed to load course details: ${err.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveMetadata = async (metadata: any) => {
    if (!id) {
      throw new Error('Course ID is missing');
    }

    // Note: SCORM courses ARE allowed to edit metadata + change publish stage.
    // Only add/delete of modules & content items is disabled for SCORM.

    // Prevent editing if course is still generating
    if (courseDetails?.stage === 'generating') {
      toast.error('Cannot edit metadata while course is still generating. Please wait for generation to complete.');
      throw new Error('Course is still generating');
    }

    // Published is final — reject even if the modal was opened before publishing.
    if (courseDetails?.stage === 'published') {
      toast.error('This course is published and can no longer be edited.');
      throw new Error('Course is published');
    }

    // Call the API to update metadata
    const response = await courseService.updateCourseMetadata(id, metadata);

    // Update local state with the response
    if (response) {
      setCourseDetails({
        ...courseDetails,
        ...metadata,
        category: metadata.category_name || courseDetails?.category,
        difficulty: metadata.difficulty || courseDetails?.difficulty,
        language: metadata.language || courseDetails?.language,
        tags: metadata.tags || courseDetails?.tags,
        prerequisites: metadata.prerequisites || courseDetails?.prerequisites,
        target_audience: metadata.target_audience || courseDetails?.target_audience,
        learning_objectives: metadata.learning_objectives || courseDetails?.learning_objectives,
        // Mirror the saved values exactly — do NOT fall back to the old image, or a
        // deleted (cleared to '') thumbnail/banner would be resurrected in the UI.
        thumbnail_image_url: metadata.thumbnail_image_url || '',
        banner_image_url: metadata.banner_image_url || '',
        thumbnail: metadata.thumbnail_image_url || '',
        banner: metadata.banner_image_url || '',
        estimated_duration: metadata.estimated_duration || courseDetails?.estimated_duration
      });

      // Update metadata state
      setCourseMetadata({
        ...courseMetadata,
        category: metadata.category_name,
        level: metadata.difficulty,
        language: metadata.language,
        tags: metadata.tags,
        prerequisites: metadata.prerequisites,
        skills: metadata.target_audience
      });

      // Refresh the course catalog so the updated thumbnail/banner (incl. deletions)
      // isn't served from the cached list on the next visit.
      queryClient.invalidateQueries({ queryKey: ['minimal-courses'] });
      if (masterCourseId) {
        queryClient.invalidateQueries({ queryKey: ['course-translations', masterCourseId] });
      }

      return response;
    }

    throw new Error('Failed to update metadata');
  };

  const handleDeleteCourse = async () => {
    if (!id) {
      throw new Error('Course ID is missing');
    }
    if (!canDeleteCourse) {
      toast.error('You do not have permission to delete courses.');
      throw new Error('Missing delete permission');
    }
    try {
      const response: any = await courseService.deleteCourse(id);
      console.log("res", response)
      // return
      if (response?.meta_data?.active_users?.length > 0) {
        return response;
      } else {
        queryClient.invalidateQueries({ queryKey: ['minimal-courses'] });
        queryClient.invalidateQueries({ queryKey: ['courses'] });
        toast.success('Course deleted successfully');
        navigate('/admin/course/course-catalog');
      }
    } catch (error: any) {
      console.error('Error deleting course:', error);
      const msg = error?.response?.data?.description || error?.response?.data?.error?.error_description || error?.response?.data?.message || error?.message || 'Failed to delete course';
      toast.error(msg);
      throw error; // Re-throw so that calling component can catch it and know not to close
    }
  };

  const handleStageChange = async (stage: CourseStage) => {
    if (!id) return;

    // Note: SCORM courses ARE allowed to change publish stage.

    // Prevent stage change if course is still generating
    if (courseDetails?.stage === 'generating') {
      toast.error('Cannot change stage while course is still generating. Please wait for generation to complete.');
      return;
    }

    // Publishing is one-way — a published course can never go back to draft.
    if (courseDetails?.stage === 'published') {
      toast.error('This course is published. Published courses cannot be changed back to draft.');
      return;
    }

    // A module with no content item blocks publishing. That is checked server-side —
    // only the API can see soft-deleted modules and content items — and its message
    // names the offending modules, so surface it verbatim below.
    try {
      const response = await courseService.updateCourseStage(id, stage as Exclude<CourseStage, 'generating'>);

      if (response.success) {
        // Update local course details with new stage
        setCourseDetails(prev => prev ? { ...prev, stage: stage } : prev);

        // Update metadata as well
        setCourseMetadata(prev => ({ ...prev, stage: stage }));

        toast.success(`Course stage updated to ${stage}`);
      } else {
        throw new Error(response.description || 'Failed to update course stage');
      }
    } catch (error: any) {
      console.error('Error updating course stage:', error);
      const message =
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update course stage';
      toast.error(message, { duration: 8000 });
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Assessment modal handlers
  const handleViewAssessment = async (assessmentId: string) => {
    setAssessmentModalState({
      isOpen: true,
      assessmentId,
      assessmentData: null,
      loading: true,
    });

    try {
      const response = await courseService.getAssessmentAdmin(assessmentId, activeLanguageCode);
      const assessmentData = response.data || response;
      setAssessmentModalState(prev => ({
        ...prev,
        assessmentData: assessmentData,
        loading: false,
      }));
    } catch (error) {
      console.error('Error fetching assessment:', error);
      toast.error('Failed to load assessment details');
      setAssessmentModalState(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const closeAssessmentModal = () => {
    setAssessmentModalState({
      isOpen: false,
      assessmentId: null,
      assessmentData: null,
      loading: false,
    });
  };

  const refreshAssessmentData = async () => {
    if (!assessmentModalState.assessmentId) return;
    try {
      const assessmentId = assessmentModalState.assessmentId;
      const response = await courseService.getAssessmentAdmin(assessmentId, activeLanguageCode);
      const data = response.data || response;
      setAssessmentModalState(prev => ({ ...prev, assessmentData: data }));
      syncPageAssessmentData(assessmentId, data);

      // Force-refresh the owning module's content tree so the module/content-item
      // assessment cards show the authoritative question count (not a stale one).
      const owningModuleId = Object.keys(moduleContents).find(mid => {
        const mod: any = moduleContents[mid]?.module;
        if (!mod) return false;
        if (mod.pre_assessment?.id === assessmentId || mod.post_assessment?.id === assessmentId) return true;
        return (mod.content_items || []).some((ci: any) =>
          ci.pre_assessment?.id === assessmentId || ci.post_assessment?.id === assessmentId
        );
      });
      if (owningModuleId) {
        await fetchModuleContent(owningModuleId, true);
      }
    } catch (error) {
      console.error('Error refreshing assessment:', error);
    }
  };

  const syncPageAssessmentData = (assessment_id: string, data: any) => {
    const patch = {
      passing_score: data.passing_score,
      total_questions: data.total_questions,
      total_points: data.total_points,
      time_limit_minutes: data.time_limit_minutes,
    };

    // Sync course-level pre/post assessment details
    if (courseDetails?.pre_assessment?.id === assessment_id) {
      setCoursePreAssessmentDetails(data);
    }
    if (courseDetails?.post_assessment?.id === assessment_id) {
      setCourseAssessmentDetails(data);
    }

    // Sync module-level assessment data in courseDetails
    if (courseDetails) {
      const updated_modules = courseDetails.modules?.map((module: any) => {
        const updated_module = { ...module };
        if (module.pre_assessment?.id === assessment_id) {
          updated_module.pre_assessment = { ...module.pre_assessment, ...patch };
        }
        if (module.post_assessment?.id === assessment_id) {
          updated_module.post_assessment = { ...module.post_assessment, ...patch };
        }
        return updated_module;
      });
      setCourseDetails((prev: any) => ({ ...prev, modules: updated_modules }));
    }

    // Sync moduleContents (used by currentModule = moduleContent?.module || module)
    setModuleContents((prev: any) => {
      const updated = { ...prev };
      for (const module_id of Object.keys(updated)) {
        const mc = updated[module_id];
        if (!mc?.module) continue;
        const mod = mc.module;
        if (mod.pre_assessment?.id === assessment_id) {
          updated[module_id] = {
            ...mc,
            module: { ...mod, pre_assessment: { ...mod.pre_assessment, ...patch } }
          };
        }
        if (mod.post_assessment?.id === assessment_id) {
          updated[module_id] = {
            ...mc,
            module: { ...mod, post_assessment: { ...mod.post_assessment, ...patch } }
          };
        }
      }
      return updated;
    });
  };

  const handleDeleteQuestion = async () => {
    if (!deletingQuestion || !assessmentModalState.assessmentId) return;
    setIsDeletingQuestion(true);
    try {
      await courseService.deleteAssessmentQuestion(
        assessmentModalState.assessmentId,
        deletingQuestion.questionId
      );
      toast.success('Question deleted successfully');
      setDeletingQuestion(null);
      await refreshAssessmentData();
    } catch (error: any) {
      const msg = error?.response?.data?.error?.error_description || 'Failed to delete question';
      toast.error(msg);
    } finally {
      setIsDeletingQuestion(false);
    }
  };

  const fetchModuleContent = async (moduleId: string, force = false) => {
    // The cache is per LANGUAGE, not just per module.
    const cachedLanguage = (moduleContents[moduleId] as any)?.__language;
    const wantedLanguage = activeLanguageCode || 'master';
    if (!force && moduleContents[moduleId] && cachedLanguage === wantedLanguage) {
      // Content already loaded, in the language currently on screen
      return;
    }

    try {
      setLoadingModules(prev => [...prev, moduleId]);
      const content = await courseService.getModuleContentTree(moduleId, activeLanguageCode);

      // Keep content items as-is, assessments will be displayed inside each content item accordion
      // Module-level assessments will be displayed separately

      setModuleContents(prev => ({
        ...prev,
        [moduleId]: { ...(content as any), __language: wantedLanguage }
      }));
    } catch (error) {
      console.error('Error loading module content:', error);
      toast.error('Failed to load module content');
    } finally {
      setLoadingModules(prev => prev.filter(id => id !== moduleId));
    }
  };

  // Refetch module content (forces reload even if already loaded)
  /**
   * Durations roll up scene → content item → module → course on the server, so
   * after any content/scene change the module and course totals on screen are
   * stale. Re-read them silently — no full-page loader, no lost expansion.
   */
  const refreshRolledUpDurations = async () => {
    if (!id) return;
    try {
      const response = await courseService.getCourseById(id);
      const data = response?.data?.data;
      if (!data) return;
      setCourseDetails(prev => (prev ? {
        ...prev,
        modules: data.modules ?? prev.modules,
        estimated_duration: data.estimated_duration ?? prev.estimated_duration,
      } : data));
    } catch (error) {
      // Non-blocking: the content itself already refreshed.
      console.error('Error refreshing durations:', error);
    }
  };

  const refetchModuleContent = async (moduleId: string) => {
    try {
      setLoadingModules(prev => [...prev, moduleId]);
      const content = await courseService.getModuleContentTree(moduleId, activeLanguageCode);

      // Stamped like fetchModuleContent, or the next cache check would see an
      // unstamped entry and refetch on every open.
      setModuleContents(prev => ({
        ...prev,
        [moduleId]: { ...(content as any), __language: activeLanguageCode || 'master' }
      }));

      await refreshRolledUpDurations();

      // Invalidate course translations query so yellow warning icons (⚠️) update immediately without needing F5 page refresh
      if (masterCourseId) {
        queryClient.invalidateQueries({ queryKey: ['course-translations', masterCourseId] });
      }

      toast.success('Content refreshed');
    } catch (error) {
      console.error('Error refreshing module content:', error);
      toast.error('Failed to refresh content');
    } finally {
      setLoadingModules(prev => prev.filter(id => id !== moduleId));
    }
  };

  const toggleModule = (moduleId: string) => {
    const isCurrentlyExpanded = expandedModules.includes(moduleId);

    if (!isCurrentlyExpanded) {
      // Expanding - fetch content if not already loaded
      fetchModuleContent(moduleId);
    }

    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [moduleId]
    );
  };

  // Default generation settings when not available
  const getDefaultGenerationSettings = (): CourseGenerationSettings => ({
    content_format: ContentFormatEnum.MARKDOWN,
    theme: SlideThemeEnum.EDUCATIONAL,
    orchestration_id: 'default'
  });

  // Get generation settings with fallback to defaults
  const getGenerationSettings = (): CourseGenerationSettings => {
    return courseDetails?.generation_settings || getDefaultGenerationSettings();
  };

  const formatDuration = (duration?: Duration | null): string => {
    if (!duration) return '0m';
    // Normalise to total seconds so per-item seconds are shown and the item
    // rows reconcile with the (seconds-summed) module/course totals — otherwise
    // items floor to whole minutes (0m + 1m + 1m) while the module keeps the
    // hidden seconds (→ 3m), and the numbers appear not to add up.
    const totalSeconds = Math.round(
      (duration.hours || 0) * 3600 + (duration.minutes || 0) * 60 + (duration.seconds || 0)
    );
    if (totalSeconds <= 0) return '0m';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (s > 0) parts.push(`${s}s`);
    return parts.join(' ') || '0m';
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'video_presentation':
        return <Video className="w-4 h-4" />;
      case 'avatar_video':
      case 'avatar_presentation':
        return <MonitorPlay className="w-4 h-4" />;
      case 'html_with_audio':
        return <Headphones className="w-4 h-4" />;
      case 'html':
        return <BookOpen className="w-4 h-4" />;
      case 'scorm':
        return <MonitorPlay className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getBloomLevelColor = (level: string): string => {
    const colors: { [key: string]: string } = {
      'Remember': 'bg-blue-100 text-blue-800',
      'Understand': 'bg-green-100 text-green-800',
      'Apply': 'bg-yellow-100 text-yellow-800',
      'Analyze': 'bg-orange-100 text-orange-800',
      'Evaluate': 'bg-purple-100 text-purple-800',
      'Create': 'bg-red-100 text-red-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!courseDetails) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Course not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <CourseHeader
        courseDetails={courseDetails}
        // Only the TRANSLATION flag, deliberately not isReadOnly. The header
        // uses readOnly to hide stage change and metadata editing — both of
        // which write to the master id and must be blocked here.
        readOnly={isViewingTranslation}
        onNavigateBack={() => navigate('/admin/course/course-catalog')}
        onOpenMetadata={() => openMetadata('basic')}
        onPreview={() => window.open(`/admin/course/preview/${id}`, '_blank')}
        onStageChange={handleStageChange}
        translations={courseTranslations}
        masterCourseId={masterCourseId}
        masterLanguageCode={
          // ALWAYS the original's language, never the one on screen.
          courseDetails?.master_language_code ||
          (courseDetails?.source_course_id
            ? // Viewing a TRANSLATION on a backend that does not yet send
              // master_language_code. The language on screen is this
              // translation's, so deriving from it would label the original
              // "Hindi" as well and list Hindi twice.
              undefined
            : // generation_settings is checked LAST but matters most: many
              // course documents carry neither language_code nor language, and
              // getLanguageCode answers 'en-US' for missing input instead of
              // admitting it cannot tell.
              getLanguageCode(
                courseDetails?.language_code ||
                  courseDetails?.language ||
                  (courseDetails as any)?.generation_settings?.language_config?.language_code ||
                  'en-US'
              ))
        }
        onSwitchLanguage={(languageCode, isMaster) => {
          // Same course id in both cases -- the master gets a bare URL, a
          // translation gets ?language=. Enrolment, progress and any
          // bookmark therefore keep pointing at one course.
          const target = `/admin/edit-course/${masterCourseId}`;
          navigate(
            isMaster
              ? target
              : `${target}?language=${encodeURIComponent(languageCode)}`
          );
        }}
        onManageTranslations={
          // Also gates whether the language button appears at all on a course
          // with no translations yet, so it must exclude SCORM: that course has
          // no Translations tab, and offering the link would open the modal on
          isMasterCourse && !isScormCourse
            ? () => openMetadata('translations')
            : undefined
        }
        onOpenSourceCourse={
          courseDetails.source_course_id
            ? () => navigate(`/admin/edit-course/${courseDetails.source_course_id}`)
            : undefined
        }
      />

      {/* Out-of-date notice. The Translations tab is on the MASTER only, so
          reading a translated course gave no sign it was behind and no way
          through. Links across rather than offering a second Re-translate
          button — one place owns the job. */}
      {/* Being re-translated RIGHT NOW. The previous version is still complete,
          so it is served rather than blocked — but never silently, or the page
          looks current while the Translations tab says otherwise. */}
      {courseDetails.source_course_id && isTranslationUpdating && (
        <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-xl p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5 animate-spin" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-indigo-900">
                This language is being updated
              </h3>
              <p className="text-sm text-indigo-800 mt-1">
                You are seeing the previous version, which is complete. Lessons
                changed in the original appear here as each one finishes — this
                page refreshes on its own, no need to reload.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hidden while updating: "out of date" is already being acted on, and
          two notices about the same thing read as two problems. */}
      {courseDetails.source_course_id && isTranslationStale && !isTranslationUpdating && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-blue-900">
                This translation is out of date
              </h3>
              <p className="text-sm text-blue-800 mt-1">
                The original course has changed since this language was produced.
                Re-translate it from the original to bring it up to date.
              </p>
            </div>
            <button
              onClick={() =>
                navigate(`/admin/edit-course/${masterCourseId}?panel=translations`)
              }
              className="flex-shrink-0 px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50"
            >
              Manage translations
            </button>
          </div>
        </div>
      )}

      {/* Partial-translation notice.
          A translated course still renders when some lessons failed — those
          lessons simply show the master's English text. Without this the page
          looks complete and the failure is only visible in the database, so
          nobody ever retries it. Says WHICH lessons, and links straight to the
          panel that can re-run them. */}
      {courseDetails.source_course_id && failedTranslation.count > 0 && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-amber-900">
                {failedTranslation.count === 1
                  ? '1 piece of this course could not be translated'
                  : `${failedTranslation.count} pieces of this course could not be translated`}
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                Those still show the original English text. Everything else on this
                page is translated.
              </p>
              {failedTranslation.items.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {failedTranslation.items.slice(0, 5).map((item, index) => (
                    <li key={index} className="text-sm text-amber-900">
                      <span className="font-medium">{item.lesson}</span>
                      {item.reason && (
                        <span className="text-amber-700"> — {item.reason}</span>
                      )}
                    </li>
                  ))}
                  {failedTranslation.items.length > 5 && (
                    <li className="text-sm text-amber-700">
                      …and {failedTranslation.items.length - 5} more
                    </li>
                  )}
                </ul>
              )}
            </div>
            <button
              onClick={() =>
                // Retries are run from the MASTER course, so go there with the
                // panel already open rather than making the admin find it.
                navigate(`/admin/edit-course/${masterCourseId}?panel=translations`)
              }
              className="px-3 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors flex-shrink-0 self-start"
            >
              Review &amp; retry
            </button>
          </div>
        </div>
      )}

      {/* Course Description */}
      {courseDetails.description && (
        <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Course Description</h2>
          <p className="text-gray-700 leading-relaxed">{courseDetails.description}</p>
        </div>
      )}


      {/* Add course-level assessments (shown for the missing slots) */}
      {canManageAssessments && id && (!courseDetails?.pre_assessment?.id || !courseDetails?.post_assessment?.id) && (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {!courseDetails?.pre_assessment?.id && (
            <button
              onClick={() => openAddAssessment('COURSE', 'PRE', id)}
              className="px-4 py-2 rounded-lg border-2 border-dashed border-blue-300 text-blue-700 text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Course Pre-Assessment
            </button>
          )}
          {!courseDetails?.post_assessment?.id && (
            <button
              onClick={() => openAddAssessment('COURSE', 'POST', id)}
              className="px-4 py-2 rounded-lg border-2 border-dashed border-purple-300 text-purple-700 text-sm font-medium hover:bg-purple-50 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Course Post-Assessment
            </button>
          )}
        </div>
      )}

      {/* Course-level Pre-Assessment */}
      {courseDetails?.pre_assessment?.id && (
        <div className="mt-6 bg-white rounded-xl border-2 border-blue-200 shadow-sm overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">Course Pre-Assessment</h3>
                    <span className="px-2 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">COURSE LEVEL</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{courseDetails.pre_assessment.title}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {loadingCoursePreAssessment ? (
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                ) : coursePreAssessmentDetails ? (
                  <>
                    <span className="px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                      Pass: {coursePreAssessmentDetails.passing_score}%
                    </span>
                    <span className="px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                      {coursePreAssessmentDetails.total_questions} Questions
                    </span>
                    <span className="px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                      {coursePreAssessmentDetails.total_points} Points
                    </span>
                    {coursePreAssessmentDetails.time_limit_minutes && (
                      <span className="px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                        {coursePreAssessmentDetails.time_limit_minutes >= 60 ? `${Math.floor(coursePreAssessmentDetails.time_limit_minutes / 60)}h${coursePreAssessmentDetails.time_limit_minutes % 60 > 0 ? ` ${coursePreAssessmentDetails.time_limit_minutes % 60}m` : ''}` : `${coursePreAssessmentDetails.time_limit_minutes} mins`}
                      </span>
                    )}
                  </>
                ) : null}
                <button
                  onClick={() => handleViewAssessment(courseDetails.pre_assessment!.id)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  View Details
                </button>
                {canManageAssessments && (
                  <button
                    onClick={() => requestDeleteAssessment(courseDetails.pre_assessment!.id, 'COURSE', 'Course Pre-Assessment')}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete assessment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {courseDetails.pre_assessment.description && (
              <p className="text-sm text-gray-700 mt-4">{courseDetails.pre_assessment.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Modules toolbar */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Modules</h2>
          {isReordering && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
        </div>
        {canEditModules && (
          <button
            onClick={() => openAddModule(courseDetails.modules?.length || 0)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Module
          </button>
        )}
      </div>

      {/* Module Accordion Layout (drag the grip handle to reorder) */}
      <div className="mt-4 space-y-4">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleModuleDragEnd}>
          <SortableContext
            items={(courseDetails.modules || []).map((m, i) => getModuleSortableId(m, i))}
            strategy={verticalListSortingStrategy}
          >
            {courseDetails.modules?.map((module, index) => {
              const moduleKey = module._id || index.toString();
              const isExpanded = expandedModules.includes(moduleKey);
              // Reorder (drag) allowed while collapsed — including on SCORM.
              const dragDisabled = !canReorderModules || isExpanded;
              return (
                <div key={moduleKey}>
                  <SortableModule id={moduleKey} disabled={dragDisabled}>
                    {({ attributes, listeners }) => (
                      <>
                        {/* Module Header */}
                        <div
                          className="p-4 cursor-pointer hover:opacity-90 transition-all"
                          onClick={() => toggleModule(moduleKey)}
                        >
                          <div className="flex items-center gap-4">
                            {/* Drag handle (collapsed modules only) */}
                            {!dragDisabled && (
                              <button
                                type="button"
                                {...attributes}
                                {...listeners}
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 p-1 -ml-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
                                title="Drag to reorder"
                                aria-label="Drag to reorder module"
                              >
                                <GripVertical className="w-5 h-5" />
                              </button>
                            )}
                            {/* Module Progress/Status */}
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                              </div>
                            </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">{module.title}</h3>
                    {module.content_items && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                        {module.content_items.length} items
                      </span>
                    )}
                    {/* Count seconds too — a module holding a single 30s scene has
                        hours=0 and minutes=0, and was hidden entirely. */}
                    {!isScormCourse && module.estimated_duration &&
                      ((module.estimated_duration.hours || 0) * 3600
                        + (module.estimated_duration.minutes || 0) * 60
                        + (module.estimated_duration.seconds || 0)) > 0 && (
                      <span className="text-xs text-gray-500">{formatDuration(module.estimated_duration)}</span>
                    )}
                  </div>
                </div>

                {canEditModules && (
                  <div className="flex-shrink-0 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openEditModule(module)}
                      className="p-1.5 rounded-lg hover:bg-blue-100 transition-colors"
                      title="Edit module details"
                    >
                      <EditIcon className="w-4 h-4 text-blue-600" />
                    </button>
                    <button
                      onClick={() => setDeletingModule(module)}
                      className="p-1.5 rounded-lg hover:bg-red-100 transition-colors"
                      title="Delete module"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                )}
                <div className="flex-shrink-0">
                  {expandedModules.includes(module._id || index.toString()) ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>

            {/* Module Content */}
            {expandedModules.includes(module._id || index.toString()) && (
              <div className="border-t border-gray-200 bg-gray-50">
                {loadingModules.includes(module._id || index.toString()) ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="p-6 space-y-6">
                    {/* Module Description */}
                    {module.description && (
                      <div className="bg-white p-4 rounded-lg border">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Module Description</h4>
                        <p className="text-sm text-gray-700">{module.description}</p>
                      </div>
                    )}

                    {(() => {
                      const moduleContent = moduleContents[module._id || index.toString()];
                      const currentModule = moduleContent?.module || module;

                      return (
                        <>

                          {/* Learning Objectives */}
                          {currentModule.learning_objectives && currentModule.learning_objectives.length > 0 && (
                            <ModuleLearningObjectives
                              objectives={currentModule.learning_objectives}
                              expanded={expandedSections.objectives}
                              onToggle={() => toggleSection('objectives')}
                              getBloomLevelColor={getBloomLevelColor}
                            />
                          )}

                          {/* Add module-level assessments (missing slots) */}
                          {canManageAssessments && module._id &&
                            (!currentModule.pre_assessment?.id || !currentModule.post_assessment?.id) && (
                            <div className="flex flex-wrap items-center gap-2">
                              {!currentModule.pre_assessment?.id && (
                                <button
                                  onClick={() => openAddAssessment('MODULE', 'PRE', module._id!)}
                                  className="px-3 py-1.5 rounded-lg border-2 border-dashed border-blue-300 text-blue-700 text-xs font-medium hover:bg-blue-50 transition-colors flex items-center gap-1.5"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Add Module Pre-Assessment
                                </button>
                              )}
                              {!currentModule.post_assessment?.id && (
                                <button
                                  onClick={() => openAddAssessment('MODULE', 'POST', module._id!)}
                                  className="px-3 py-1.5 rounded-lg border-2 border-dashed border-green-300 text-green-700 text-xs font-medium hover:bg-green-50 transition-colors flex items-center gap-1.5"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Add Module Post-Assessment
                                </button>
                              )}
                            </div>
                          )}

                          {/* Module Pre-Assessment */}
                          {currentModule.pre_assessment && currentModule.pre_assessment.id && (
                            <div className="bg-white rounded-lg border-2 border-blue-200">
                              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                                      <ClipboardCheck className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="text-base font-bold text-gray-900">Module Pre-Assessment</h4>
                                      <p className="text-sm text-gray-600">{currentModule.pre_assessment.title}</p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    {currentModule.pre_assessment.required && (
                                      <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-xs font-medium">Required</span>
                                    )}
                                    <span className="px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                                      Pass: {currentModule.pre_assessment.passing_score}%
                                    </span>
                                    <span className="px-3 py-1 bg-white text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                                      {currentModule.pre_assessment.total_questions} Questions
                                    </span>
                                    <button
                                      onClick={() => handleViewAssessment(currentModule.pre_assessment!.id)}
                                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                                    >
                                      View Details
                                    </button>
                                    {canManageAssessments && (
                                      <button
                                        onClick={() => requestDeleteAssessment(currentModule.pre_assessment!.id, 'MODULE', 'Module Pre-Assessment', module._id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete assessment"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {currentModule.pre_assessment.description && (
                                  <p className="text-sm text-gray-700 mt-3">{currentModule.pre_assessment.description}</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Content Items — render for any editable (non-SCORM)
                              module even when empty, so a module with no content
                              items still shows the "Add Content Item" button. */}
                          {((currentModule.content_items && currentModule.content_items.length > 0) ||
                            (module._id && !isScormCourse)) && (
                            <ContentItems
                              items={currentModule.content_items || []}
                              expanded={true}
                              onToggle={() => { }}
                              getContentTypeIcon={getContentTypeIcon}
                              formatDuration={formatDuration}
                              theme={getGenerationSettings().theme}
                              videoRenderingMode={getGenerationSettings().html_slides_config?.video_rendering_mode || 'animated'}
                              contentFormat={getGenerationSettings().content_format}
                              onViewAssessment={handleViewAssessment}
                              moduleId={module._id}
                              onRefresh={refetchModuleContent}
                              courseLanguage={getLanguageCode(courseDetails?.language_code || courseDetails?.language || 'en-US')}
                              isGenerating={courseDetails?.stage === 'generating'}
                              isScormCourse={isScormCourse}
                              structureLocked={isStructureLocked || isReadOnly}
                              readOnly={isReadOnly}
                              onAddAssessment={(type, contentItemId) =>
                                openAddAssessment('CONTENT_ITEM', type, contentItemId, module._id)
                              }
                              onDeleteAssessment={(assessmentId, label) =>
                                requestDeleteAssessment(assessmentId, 'CONTENT_ITEM', label, module._id)
                              }
                            />
                          )}

                          {/* Module Post-Assessment */}
                          {currentModule.post_assessment && currentModule.post_assessment.id && (
                            <div className="bg-white rounded-lg border-2 border-green-200">
                              <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                                      <ClipboardCheck className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                      <h4 className="text-base font-bold text-gray-900">Module Post-Assessment</h4>
                                      <p className="text-sm text-gray-600">{currentModule.post_assessment.title}</p>
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    {currentModule.post_assessment.required && (
                                      <span className="px-3 py-1 bg-green-600 text-white rounded-full text-xs font-medium">Required</span>
                                    )}
                                    <span className="px-3 py-1 bg-white text-green-700 rounded-full text-xs font-medium border border-green-200">
                                      Pass: {currentModule.post_assessment.passing_score}%
                                    </span>
                                    <span className="px-3 py-1 bg-white text-green-700 rounded-full text-xs font-medium border border-green-200">
                                      {currentModule.post_assessment.total_questions} Questions
                                    </span>
                                    <button
                                      onClick={() => handleViewAssessment(currentModule.post_assessment!.id)}
                                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                                    >
                                      View Details
                                    </button>
                                    {canManageAssessments && (
                                      <button
                                        onClick={() => requestDeleteAssessment(currentModule.post_assessment!.id, 'MODULE', 'Module Post-Assessment', module._id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete assessment"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {currentModule.post_assessment.description && (
                                  <p className="text-sm text-gray-700 mt-3">{currentModule.post_assessment.description}</p>
                                )}
                              </div>
                            </div>
                          )}

                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
                        </>
                      )}
                  </SortableModule>
                </div>
              );
            })}
          </SortableContext>
        </DndContext>
      </div>

      {/* Course-Level Post-Assessment */}
      {courseDetails?.post_assessment?.id && (
        <div className="mt-6 bg-white rounded-xl border-2 border-purple-200 shadow-sm overflow-hidden">
          <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Award className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-gray-900">Final Course Assessment</h3>
                    <span className="px-2 py-1 bg-purple-600 text-white rounded-full text-xs font-medium">COURSE LEVEL</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{courseDetails.post_assessment.title}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {loadingCourseAssessment ? (
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                ) : courseAssessmentDetails ? (
                  <>
                    <span className="px-3 py-1 bg-white text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                      Pass: {courseAssessmentDetails.passing_score}%
                    </span>
                    <span className="px-3 py-1 bg-white text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                      {courseAssessmentDetails.total_questions} Questions
                    </span>
                    <span className="px-3 py-1 bg-white text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                      {courseAssessmentDetails.total_points} Points
                    </span>
                    {courseAssessmentDetails.time_limit_minutes && (
                      <span className="px-3 py-1 bg-white text-purple-700 rounded-full text-xs font-medium border border-purple-200">
                        {courseAssessmentDetails.time_limit_minutes >= 60 ? `${Math.floor(courseAssessmentDetails.time_limit_minutes / 60)}h${courseAssessmentDetails.time_limit_minutes % 60 > 0 ? ` ${courseAssessmentDetails.time_limit_minutes % 60}m` : ''}` : `${courseAssessmentDetails.time_limit_minutes} mins`}
                      </span>
                    )}
                  </>
                ) : null}
                <button
                  onClick={() => handleViewAssessment(courseDetails.post_assessment!.id)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
                >
                  View Details
                </button>
                {canManageAssessments && (
                  <button
                    onClick={() => requestDeleteAssessment(courseDetails.post_assessment!.id, 'COURSE', 'Final Course Assessment')}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete assessment"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {courseDetails.post_assessment.description && (
              <p className="text-sm text-gray-700 mt-4">{courseDetails.post_assessment.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Metadata Modal */}
      <MetadataModal
        isOpen={isMetadataModalOpen}
        onClose={() => setIsMetadataModalOpen(false)}
        metadata={courseMetadata}
        courseDetails={courseDetails}
        onSave={handleSaveMetadata}
        onDelete={handleDeleteCourse}
        canDelete={canDeleteCourse}
        isPublished={isPublished}
        initialTab={metadataInitialTab}
      />

      {/* Assessment Admin Viewer Modal */}
      {assessmentModalState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Assessment Details</h2>
              <div className="flex items-center gap-2">
                {/* A published course is frozen — its assessments included. */}
                {!isReadOnly && (
                  <button
                    onClick={() => setShowAddQuestionModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Question
                  </button>
                )}
                <button
                  onClick={closeAssessmentModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Content - Reuse the same modal content from ContentItems */}
            <div className="flex-1 overflow-y-auto p-6">
              {assessmentModalState.loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Loading assessment...</span>
                </div>
              ) : assessmentModalState.assessmentData ? (
                <div className="space-y-6">
                  {/* Assessment Header */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {assessmentModalState.assessmentData.title}
                    </h3>
                    {assessmentModalState.assessmentData.description && (
                      <p className="text-gray-600 mb-4">{assessmentModalState.assessmentData.description}</p>
                    )}

                    {/* Summary Card - Total Points, Passing Score & Settings */}
                    <AssessmentSummaryCard
                      assessmentData={assessmentModalState.assessmentData}
                      assessmentId={assessmentModalState.assessmentId!}
                      readOnly={isReadOnly}
                      onSettingsUpdated={(updated_data) => {
                        setAssessmentModalState(prev => ({ ...prev, assessmentData: updated_data }));
                        syncPageAssessmentData(assessmentModalState.assessmentId!, updated_data);
                      }}
                    />

                    <div className="flex flex-wrap gap-2 mb-4 mt-4">
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                        {assessmentModalState.assessmentData.assessment_type} Assessment
                      </span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                        {assessmentModalState.assessmentData.assessment_level} Level
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {assessmentModalState.assessmentData.total_questions} Questions
                      </span>
                    </div>

                  </div>

                  {/* Questions */}
                  <div className="space-y-6">
                    {assessmentModalState.assessmentData.questions?.map((question: any, idx: number) => (
                      <div key={question.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                        {/* Question Header */}
                        <div className="flex items-start gap-3 mb-4">
                          <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-lg font-medium text-gray-900 mb-2">{question.question_text}</p>
                              {!isReadOnly && (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => setEditingAssessmentQuestion({
                                    question,
                                    allQuestions: assessmentModalState.assessmentData.questions,
                                    assessmentId: assessmentModalState.assessmentId!,
                                  })}
                                  className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                  title="Edit question"
                                >
                                  <EditIcon className="w-5 h-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletingQuestion({
                                    questionId: question.id,
                                    questionText: question.question_text,
                                  })}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete question"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                                {question.question_type}
                              </span>
                              <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                                {question.points} pts
                              </span>
                              {question.difficulty_level && (
                                <span className={`px-2 py-1 rounded text-xs ${
                                  question.difficulty_level === 'EASY' ? 'bg-green-100 text-green-700' :
                                  question.difficulty_level === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {question.difficulty_level}
                                </span>
                              )}
                              {question.bloom_level && (
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                  {question.bloom_level}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Options (for SINGLE_CHOICE, MULTIPLE_CHOICE) */}
                        {question.options && question.options.length > 0 && (
                          <div className="space-y-2 ml-11">
                            {question.options.map((option: any) => (
                              <div
                                key={option.id}
                                className={`p-3 rounded-lg border-2 ${
                                  option.is_correct
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-300 bg-white'
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  {option.is_correct && (
                                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                  )}
                                  <div className="flex-1">
                                    <p className={`text-sm ${option.is_correct ? 'font-medium text-green-900' : 'text-gray-700'}`}>
                                      {option.text}
                                    </p>
                                    {option.explanation && (
                                      <p className="text-xs text-gray-600 mt-1 italic">
                                        💡 {option.explanation}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* TRUE_FALSE Answer fallback for old courses without options */}
                        {question.question_type === 'TRUE_FALSE' && (!question.options || question.options.length === 0) && question.correct_answer && (
                          <div className="ml-11">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border-2 border-green-500 rounded-lg">
                              <CheckCircle2 className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-green-900">
                                Correct Answer: {question.correct_answer.toUpperCase()}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* TEXT_ANSWER Keywords */}
                        {question.question_type === 'TEXT_ANSWER' && question.answer_keywords && (
                          <div className="ml-11">
                            <div className="p-3 bg-green-50 border-2 border-green-500 rounded-lg">
                              <p className="text-sm font-medium text-green-900 mb-2">
                                <CheckCircle2 className="w-4 h-4 inline mr-1" />
                                Accepted Answers:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {question.answer_keywords.map((keyword: string, kidx: number) => (
                                  <span key={kidx} className="px-2 py-1 bg-white text-green-700 rounded text-sm border border-green-200">
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                              {question.case_sensitive !== undefined && (
                                <p className="text-xs text-gray-600 mt-2">
                                  Case sensitive: {question.case_sensitive ? 'Yes' : 'No'}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Question Explanation */}
                        {question.explanation && (
                          <div className="ml-11 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm font-medium text-blue-900 mb-1">📖 Explanation:</p>
                            <p className="text-sm text-blue-800">{question.explanation}</p>
                          </div>
                        )}

                        {/* Hint */}
                        {question.hint && (
                          <div className="ml-11 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm font-medium text-yellow-900 mb-1">💡 Hint:</p>
                            <p className="text-sm text-yellow-800">{question.hint}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Failed to load assessment data</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-blue-200 p-4 flex justify-end">
              <button
                onClick={closeAssessmentModal}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Assessment Question Modal */}
      {editingAssessmentQuestion && (
        <EditAssessmentQuestionModal
          assessmentId={editingAssessmentQuestion.assessmentId}
          question={editingAssessmentQuestion.question}
          allQuestions={editingAssessmentQuestion.allQuestions}
          onClose={() => setEditingAssessmentQuestion(null)}
          onSuccess={async () => {
            setEditingAssessmentQuestion(null);
            await refreshAssessmentData();
          }}
        />
      )}

      {/* Add Assessment Question Modal */}
      {showAddQuestionModal && assessmentModalState.assessmentId && (
        <AddAssessmentQuestionModal
          assessmentId={assessmentModalState.assessmentId}
          onClose={() => setShowAddQuestionModal(false)}
          onSuccess={async () => {
            setShowAddQuestionModal(false);
            await refreshAssessmentData();
          }}
        />
      )}

      {/* Delete Question Confirmation */}
      <ConfirmationModal
        isOpen={!!deletingQuestion}
        type="danger"
        title="Delete Question"
        message={`Are you sure you want to delete this question? "${deletingQuestion?.questionText?.substring(0, 80)}${(deletingQuestion?.questionText?.length || 0) > 80 ? '...' : ''}"`}
        confirmText={isDeletingQuestion ? 'Deleting...' : 'Delete Question'}
        cancelText="Cancel"
        onConfirm={handleDeleteQuestion}
        onCancel={() => setDeletingQuestion(null)}
      />

      {/* Add / Edit Module Modal */}
      <ModuleFormModal
        isOpen={moduleFormState.isOpen}
        mode={moduleFormState.mode}
        module={moduleFormState.module}
        insertPosition={moduleFormState.mode === 'add' ? moduleFormState.insertIndex + 1 : undefined}
        onSave={handleSaveModule}
        onClose={closeModuleForm}
      />

      {/* Delete Module Confirmation */}
      <ConfirmationModal
        isOpen={!!deletingModule}
        type="danger"
        title="Delete Module"
        message={`Are you sure you want to delete "${deletingModule?.title || 'this module'}"? This removes the module and all of its content items. This action cannot be undone.`}
        confirmText={isDeletingModule ? 'Deleting...' : 'Delete Module'}
        cancelText="Cancel"
        onConfirm={handleDeleteModuleConfirm}
        onCancel={() => setDeletingModule(null)}
      />

      {/* Add Assessment Modal (course / module / content-item) */}
      <AddAssessmentModal
        isOpen={addAssessmentState.isOpen}
        level={addAssessmentState.level}
        type={addAssessmentState.type}
        onSave={handleCreateAssessment}
        onClose={closeAddAssessment}
      />

      {/* Delete Assessment Confirmation */}
      <ConfirmationModal
        isOpen={!!deletingAssessment}
        type="danger"
        title="Delete Assessment"
        message={`Are you sure you want to delete "${deletingAssessment?.label || 'this assessment'}"? This removes the assessment and all its questions. This action cannot be undone.`}
        confirmText={isDeletingAssessment ? 'Deleting...' : 'Delete Assessment'}
        cancelText="Cancel"
        onConfirm={handleDeleteAssessment}
        onCancel={() => setDeletingAssessment(null)}
      />

    </div>
  );
}

/** Toggle switch component for assessment settings */
function AssessmentToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm" />
    </label>
  );
}

/** Summary card showing total points, passing score & editable assessment settings */
function AssessmentSummaryCard({
  assessmentData,
  assessmentId,
  onSettingsUpdated,
  readOnly = false,
}: {
  assessmentData: any;
  assessmentId: string;
  onSettingsUpdated: (data: any) => void;
  /** Published courses freeze their assessments — settings become view-only. */
  readOnly?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    passing_score: assessmentData.passing_score ?? 0,
    time_limit_minutes: assessmentData.time_limit_minutes ?? '',
    max_attempts: assessmentData.max_attempts ?? '',
    allow_retry: assessmentData.allow_retry ?? true,
    shuffle_options: assessmentData.shuffle_options ?? true,
    show_feedback: assessmentData.show_feedback ?? true,
    show_correct_answers: assessmentData.show_correct_answers ?? true,
  });
  const [scoreError, setScoreError] = useState('');

  const resetForm = () => {
    setFormData({
      passing_score: assessmentData.passing_score ?? 0,
      time_limit_minutes: assessmentData.time_limit_minutes ?? '',
      max_attempts: assessmentData.max_attempts ?? '',
      allow_retry: assessmentData.allow_retry ?? true,
      shuffle_options: assessmentData.shuffle_options ?? true,
      show_feedback: assessmentData.show_feedback ?? true,
      show_correct_answers: assessmentData.show_correct_answers ?? true,
    });
    setScoreError('');
  };

  const handleSave = async () => {
    if (formData.passing_score === '' || formData.passing_score === null || formData.passing_score === undefined) {
      setScoreError('Enter a number');
      return;
    }
    if (formData.passing_score < 0) {
      setScoreError('Score cannot be negative');
      return;
    }
    setScoreError('');

    const settings: any = {
      passing_score: Math.min(100, Math.max(0, Math.round(Number(formData.passing_score)))),
      time_limit_minutes: formData.time_limit_minutes === '' ? null : Number(formData.time_limit_minutes),
      max_attempts: formData.max_attempts === '' ? null : Number(formData.max_attempts),
      allow_retry: formData.allow_retry,
      shuffle_options: formData.shuffle_options,
      show_feedback: formData.show_feedback,
      show_correct_answers: formData.show_correct_answers,
    };

    setIsSaving(true);
    try {
      const response = await courseService.updateAssessmentSettings(assessmentId, settings);
      const updated = response.data || response;
      onSettingsUpdated(updated);
      setIsEditing(false);
      toast.success('Assessment settings updated');
    } catch (error: any) {
      const msg = error?.response?.data?.error?.error_description || 'Failed to update settings';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats Row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
          <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Total Points</p>
          <p className="text-3xl font-bold text-green-900">{assessmentData.total_points}</p>
          <p className="text-xs text-green-600 mt-1">
            Across {assessmentData.total_questions} question{assessmentData.total_questions !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
          <p className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Passing Score</p>
          <p className="text-3xl font-bold text-orange-900">{assessmentData.passing_score}%</p>
          <p className="text-xs text-orange-600 mt-1">Minimum to pass</p>
        </div>
      </div>

      {/* Settings Section */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-700">Assessment Settings</p>
          {!isEditing ? (
            !readOnly && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <EditIcon className="w-3.5 h-3.5" />
                Edit
              </button>
            )
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setIsEditing(false); resetForm(); }}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="p-4 space-y-4">
            {/* Number Inputs Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Passing Score */}
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <Award className="w-4 h-4 text-orange-500" />
                  Passing Score (%)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.passing_score}
                  onChange={(e) => {
                    setScoreError('');
                    if (e.target.value === '') { setFormData(prev => ({ ...prev, passing_score: '' as any })); return; }
                    const val = Math.round(Number(e.target.value));
                    if (val < 0) return;
                    if (val > 100) { setFormData(prev => ({ ...prev, passing_score: 100 })); return; }
                    setFormData(prev => ({ ...prev, passing_score: val }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="0-100"
                />
                {scoreError && <p className="text-red-500 text-xs mt-1">{scoreError}</p>}
              </div>

              {/* Time Limit */}
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <Clock className="w-4 h-4 text-amber-500" />
                  Time Limit (mins)
                </label>
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={formData.time_limit_minutes}
                  onChange={(e) => {
                    if (e.target.value === '') { setFormData(prev => ({ ...prev, time_limit_minutes: '' })); return; }
                    const val = Math.round(Number(e.target.value));
                    if (val < 0) return;
                    if (val > 1440) { setFormData(prev => ({ ...prev, time_limit_minutes: 1440 })); return; }
                    setFormData(prev => ({ ...prev, time_limit_minutes: val }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="No limit"
                />
                <p className="text-xs text-gray-400 mt-1">1 - 1440 mins (24 hrs). Leave empty for unlimited</p>
              </div>

              {/* Max Attempts */}
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                  <Repeat className="w-4 h-4 text-cyan-500" />
                  Max Attempts
                </label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={formData.max_attempts}
                  onChange={(e) => {
                    if (e.target.value === '') { setFormData(prev => ({ ...prev, max_attempts: '' })); return; }
                    const val = Math.round(Number(e.target.value));
                    if (val < 0) return;
                    if (val > 100) { setFormData(prev => ({ ...prev, max_attempts: 100 })); return; }
                    setFormData(prev => ({ ...prev, max_attempts: val }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-shadow [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder="Unlimited"
                />
                <p className="text-xs text-gray-400 mt-1">1 - 100. Leave empty for unlimited</p>
              </div>
            </div>

            {/* Toggle Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 ${formData.allow_retry ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-teal-100 text-teal-600"><RefreshCw className="w-4 h-4" /></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Allow Retry</p>
                    <p className="text-xs text-gray-500">Learners can reattempt</p>
                  </div>
                </div>
                <AssessmentToggle checked={formData.allow_retry} onChange={(val) => setFormData(prev => ({ ...prev, allow_retry: val }))} />
              </div>

              <div className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 ${formData.shuffle_options ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-pink-100 text-pink-600"><Shuffle className="w-4 h-4" /></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Shuffle Options</p>
                    <p className="text-xs text-gray-500">Randomize answer order</p>
                  </div>
                </div>
                <AssessmentToggle checked={formData.shuffle_options} onChange={(val) => setFormData(prev => ({ ...prev, shuffle_options: val }))} />
              </div>

              <div className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 ${formData.show_feedback ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-violet-100 text-violet-600"><MessageSquare className="w-4 h-4" /></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Show Feedback</p>
                    <p className="text-xs text-gray-500">Display answer feedback</p>
                  </div>
                </div>
                <AssessmentToggle checked={formData.show_feedback} onChange={(val) => setFormData(prev => ({ ...prev, show_feedback: val }))} />
              </div>

              <div className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-200 ${formData.show_correct_answers ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600"><Eye className="w-4 h-4" /></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Show Correct Answers</p>
                    <p className="text-xs text-gray-500">Reveal answers after attempt</p>
                  </div>
                </div>
                <AssessmentToggle checked={formData.show_correct_answers} onChange={(val) => setFormData(prev => ({ ...prev, show_correct_answers: val }))} />
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {/* Number settings row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-semibold text-gray-500">Time Limit</span>
                </div>
                <p className="text-lg font-bold text-gray-900">{assessmentData.time_limit_minutes ? `${assessmentData.time_limit_minutes} min` : 'No limit'}</p>
              </div>
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <Repeat className="w-4 h-4 text-cyan-500" />
                  <span className="text-xs font-semibold text-gray-500">Max Attempts</span>
                </div>
                <p className="text-lg font-bold text-gray-900">{assessmentData.max_attempts ?? 'Unlimited'}</p>
              </div>
              <div className="p-3 rounded-xl border border-gray-200 bg-gray-50/50">
                <div className="flex items-center gap-2 mb-1">
                  <RefreshCw className="w-4 h-4 text-teal-500" />
                  <span className="text-xs font-semibold text-gray-500">Allow Retry</span>
                </div>
                <p className={`text-lg font-bold ${assessmentData.allow_retry ? 'text-teal-600' : 'text-gray-400'}`}>{assessmentData.allow_retry ? 'Yes' : 'No'}</p>
              </div>
            </div>

            {/* Toggle settings row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all ${assessmentData.shuffle_options ? 'bg-pink-50/50 border-pink-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className={`p-1.5 rounded-lg ${assessmentData.shuffle_options ? 'bg-pink-100 text-pink-600' : 'bg-gray-200 text-gray-400'}`}><Shuffle className="w-4 h-4" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Shuffle Options</p>
                  <p className={`text-xs ${assessmentData.shuffle_options ? 'text-pink-600' : 'text-gray-400'}`}>{assessmentData.shuffle_options ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
              <div className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all ${assessmentData.show_feedback ? 'bg-violet-50/50 border-violet-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className={`p-1.5 rounded-lg ${assessmentData.show_feedback ? 'bg-violet-100 text-violet-600' : 'bg-gray-200 text-gray-400'}`}><MessageSquare className="w-4 h-4" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Show Feedback</p>
                  <p className={`text-xs ${assessmentData.show_feedback ? 'text-violet-600' : 'text-gray-400'}`}>{assessmentData.show_feedback ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
              <div className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all ${assessmentData.show_correct_answers ? 'bg-emerald-50/50 border-emerald-200' : 'bg-gray-50/80 border-gray-200'}`}>
                <div className={`p-1.5 rounded-lg ${assessmentData.show_correct_answers ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-400'}`}><Eye className="w-4 h-4" /></div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Correct Answers</p>
                  <p className={`text-xs ${assessmentData.show_correct_answers ? 'text-emerald-600' : 'text-gray-400'}`}>{assessmentData.show_correct_answers ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}